import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Accordion,
  Grid
} from 'semantic-ui-react'
import ReactMarkdown from 'react-markdown'
// Local
import { CONDITION } from '../../../constant'
import {
  conditionClaimSource,
  condition as conditionUtils,
  allowConditionDismiss
} from '../../common/conditionUtils'
import ConditionAddressedDate from '../common/conditionAddressedDate'
import ConditionDismissButton from '../common/conditionDismissButton'
import ConditionEvidence from '../common/conditionEvidence'
import PhysicianConditionHeaderTitle from './physicianConditionHeaderTitle'
import PhysicianConditionTags from './physicianConditionTags'
import {
  EncounterConditionDisplay,
  User
} from '../../authenticated/types'
import { RootState } from '../../../store'
import { ObjectValues } from '../../../types/types'
import If from '../../common/if'

const SafeGrid = Grid as unknown as ComponentType<any>

type PhysicianConditionOwnProps = {
  condition: EncounterConditionDisplay
  mode?: string | null
  active?: boolean
  hccChild?: boolean
  displayIndex?: number
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    condition: EncounterConditionDisplay | string | null
  ) => void
}

type PhysicianConditionReduxProps = {
  currentUser: User
}

type PhysicianConditionProps =
  PhysicianConditionOwnProps &
  PhysicianConditionReduxProps

/**
 * Provides a component that renders a Physician condition view
 *
 * @param condition Condition entity
 * @param mode View mode. Options: "smart", null. Default: null
 * @param active If true, display condition expanded. Default: false
 * @param hccChild True if condition is a member of an HCC display group
 * @param displayIndex Condition display index relative to its display group
 * @param onClickTitle Callback for condition accordion title click
 *        Arguments: condition
 * @param onConditionAction Callback for condition action
 *        Arguments action, condition
 */
function PhysicianCondition(
  props: PhysicianConditionProps
): JSX.Element {
  const {
    condition,
    mode: modeProp,
    active: isActiveProp,
    hccChild: isHccChild,
    displayIndex: displayIndexProp,
    onConditionAction,
    currentUser
  } = props

  const [ isActive, setIsActive ] = useState<boolean>(
    isActiveProp === true
  )

  const handleClickTitle = () => {
    if (isHccChild === true) {
      return
    }

    const activate = CONDITION.ACTION.ACTIVATE
    const deactivate = CONDITION.ACTION.DEACTIVATE
    const read = CONDITION.ACTION.READ

    if (isActive === true) {
      setIsActive(false)
      onConditionAction(deactivate, condition)
    } else {
      setIsActive(true)
      onConditionAction(activate, condition)
      onConditionAction(read, condition)
    }
  }

  const handleClickDismiss = () => {
    if (condition.conditionCode === CONDITION.CODE.DISMISSED) {
      onConditionAction(CONDITION.ACTION.MARK_OPEN, condition)
    } else {
      onConditionAction(CONDITION.ACTION.MARK_DISMISSED, condition)
    }
  }

  const claimSource = conditionClaimSource(condition)
  const shouldAllowDismiss = allowConditionDismiss(condition, currentUser)
  const clazz = displayIndexProp && displayIndexProp === 0
    ? 'first-accordion'
    : ''

  return (
    <div className="condition">
      <Accordion.Title
        active={ isActive }
        onClick={ handleClickTitle }
        index={ condition.id }
        className={ clazz }
      >
        <PhysicianConditionHeaderTitle
          condition={ condition }
          mode={ modeProp === 'smart' ? 'smart' : null }
          hccChild={ isHccChild === true }
          active={ isActive }
          onConditionAction={ onConditionAction as unknown as (action: any, condition: EncounterConditionDisplay) => void }
        />
      </Accordion.Title>
      <Accordion.Content active={ isActive }>
        <SafeGrid
          className="internal-condition"
          columns={ 1 }
          divided
          stackable
        >
          <Grid.Row className="first-row">
            <Grid.Column>
              <div>
                <ConditionEvidence
                  condition={ condition }
                  currentUser={ currentUser }
                />
                <ReactMarkdown linkTarget="_blank">
                  { condition.content || '' }
                </ReactMarkdown>
                <ConditionAddressedDate condition={ condition } />
              </div>
            </Grid.Column>
          </Grid.Row>
          <If isTrue={ !!claimSource }>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <span className="footnote">
                  { `Initial source of condition: ${claimSource}` }
                </span>
              </Grid.Column>
            </Grid.Row>
          </If>
          <If isTrue={ conditionUtils.hasPractitionerTags(condition) }>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <PhysicianConditionTags
                  condition={ condition }
                  onConditionAction={
                    onConditionAction as unknown as (
                      action: any,
                      condition: EncounterConditionDisplay[]
                    ) => void
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </If>
          <If isTrue={ shouldAllowDismiss }>
            <Grid.Row>
              <Grid.Column textAlign="center">
                <ConditionDismissButton
                  condition={ condition }
                  onClick={ handleClickDismiss }
                />
              </Grid.Column>
            </Grid.Row>
          </If>
        </SafeGrid>
      </Accordion.Content>
    </div>
  )
}

const mapStateToProps = (state: RootState): PhysicianConditionReduxProps => ({
  currentUser: state.user
})

export default connect(
  mapStateToProps,
  {}
)(PhysicianCondition)
